/** Station-local time formatting.
 *
 *  Every timestamp in this application is displayed in the station's zone,
 *  never the browser's. The whole network is Asia/Tbilisi (UTC+4, no DST) and
 *  the people reading these charts are in it, but a laptop set to UTC — or a
 *  colleague opening a shared link from Berlin — would otherwise see every
 *  reading shifted by hours with nothing on screen admitting it.
 *
 *  This is the same class of error as reading a measurement_1d bucket back
 *  without AT TIME ZONE, which is documented in HANDBOOK.md as a trap because it
 *  has already been made once in this codebase.
 */

export const STATION_TZ = 'Asia/Tbilisi'

const cache = new Map<string, Intl.DateTimeFormat>()

function formatter(timeZone: string, options: Intl.DateTimeFormatOptions) {
  const key = `${timeZone}|${JSON.stringify(options)}`
  let existing = cache.get(key)
  if (!existing) {
    existing = new Intl.DateTimeFormat('en-GB', { ...options, timeZone })
    cache.set(key, existing)
  }
  return existing
}

/** 2026-07-21 14:30 */
export function formatDateTime(value: Date | string, tz = STATION_TZ): string {
  const date = typeof value === 'string' ? new Date(value) : value
  const parts = formatter(tz, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`
}

/** 2026-07-21 */
export function formatDate(value: Date | string, tz = STATION_TZ): string {
  const date = typeof value === 'string' ? new Date(value) : value
  const parts = formatter(tz, {
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** Thu 29 Oct 2026 */
export function formatWeekdayDate(value: Date | string, tz = STATION_TZ): string {
  const date = typeof value === 'string' ? new Date(value) : value
  const parts = formatter(tz, {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
  }).formatToParts(date)
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('weekday')} ${get('day')} ${get('month')} ${get('year')}`
}

/** 14:30 */
export function formatTime(value: Date | string, tz = STATION_TZ): string {
  const date = typeof value === 'string' ? new Date(value) : value
  return formatter(tz, {
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date)
}

/** Axis labels: dense enough to read, sparse enough not to collide.
 *
 *  ECharts formats time axes in the browser's zone by default, which is the
 *  bug this whole module exists to prevent, so the axis is given explicit
 *  strings instead. Day over time, always: every chart is stored readings,
 *  so there is no daily view whose ticks would be dates alone.
 *
 *  A value that is no instant writes nothing. ECharts hands a slider's label
 *  formatter NaN when a chart has no points, and Intl throws on an invalid
 *  date, which took the whole page with it (2026-11-02).
 */
export function formatAxisLabel(value: number, tz = STATION_TZ): string {
  if (!Number.isFinite(value)) return ''
  const date = new Date(value)
  const day = formatter(tz, { month: 'short', day: '2-digit' }).format(date)
  return `${day}\n${formatTime(date, tz)}`
}

/** "3 days ago", for station health. Absolute dates are shown alongside;
 *  this is for scanning a list, not for reading a value off.
 *
 *  Counted in station-local calendar days, not elapsed hours. A reading at
 *  23:50 yesterday is 15 hours old at midday today, which rounds down to
 *  zero — and a station card reading "today" when the last reading was
 *  yesterday hides exactly the staleness the card exists to show.
 */
export function relativeDays(value: string | null, tz = STATION_TZ,
                             now: Date = new Date()): string {
  if (!value) return 'never'
  const days = localDaysBetween(new Date(value), now, tz)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 60) return `${days} days ago`
  // Past a couple of months the day count stops being readable — a first
  // reading in 2023 renders as "1003 days ago", which nobody converts in
  // their head. The exact date is shown alongside, so this only has to convey
  // the scale.
  const months = Math.round(days / 30.44)
  if (months < 24) return `${months} months ago`
  const years = days / 365.25
  return `${years.toFixed(years < 10 ? 1 : 0)} years ago`
}

/** Whole local calendar days from `from` to `to`. */
export function localDaysBetween(from: Date, to: Date, tz = STATION_TZ): number {
  const asDay = (date: Date) => Date.parse(`${formatDate(date, tz)}T00:00:00Z`)
  return Math.round((asDay(to) - asDay(from)) / 86_400_000)
}

/** Midnight local, `days` ago, as an absolute instant.
 *
 *  Built by walking the station-local calendar rather than subtracting
 *  milliseconds, so a range labelled "7 days" starts at a day boundary the
 *  reader recognises instead of at whatever time it happens to be now.
 */
export function daysAgo(days: number, tz = STATION_TZ): Date {
  const now = new Date()
  const local = formatDate(new Date(now.getTime() - days * 86_400_000), tz)
  // Asia/Tbilisi is UTC+4 with no DST, so local midnight is a fixed offset.
  // A station in a DST zone would need the offset resolved per date.
  return new Date(`${local}T00:00:00${offsetFor(tz)}`)
}

/** The zone's UTC offset as +HH:MM, read from Intl rather than hard-coded. */
export function offsetFor(tz = STATION_TZ, at = new Date()): string {
  const parts = formatter(tz, { timeZoneName: 'longOffset' }).formatToParts(at)
  const name = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT+00:00'
  return name.replace('GMT', '') || '+00:00'
}

function offsetMinutes(tz: string, at: Date): number {
  const offset = offsetFor(tz, at)
  const match = offset.match(/^([+-])(\d{2}):(\d{2})$/)
  if (!match) return 0
  const [, sign, hours, minutes] = match
  return (sign === '-' ? -1 : 1) * (Number(hours) * 60 + Number(minutes))
}

/** `<input type="datetime-local">` value for an instant, in station time.
 *
 *  The input has no timezone of its own: it shows and returns a bare wall
 *  clock, which the browser would otherwise pair with the *browser's* zone.
 *  These two functions are what keep the range the user types meaning the
 *  same thing as every timestamp displayed next to it.
 */
export function toLocalInput(value: Date, tz = STATION_TZ): string {
  return `${formatDate(value, tz)}T${formatTime(value, tz)}`
}

/** Read a wall clock back as an instant, interpreting it in station time. */
export function fromLocalInput(value: string, tz = STATION_TZ): Date {
  // Matched strictly rather than handed to Date.parse, which accepts partial
  // ISO dates: "2026-07" parses happily as the first of July, so a range the
  // user is halfway through typing would silently become a real query.
  const shape = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(:\d{2})?$/
  if (!shape.test(value)) return new Date(NaN)

  const asIfUtc = Date.parse(`${value.length === 16 ? `${value}:00` : value}Z`)
  if (Number.isNaN(asIfUtc)) return new Date(NaN)
  // Two passes: the first lands in the right neighbourhood, the second
  // settles a guess that fell the other side of a DST change. Asia/Tbilisi
  // has no DST and converges on the first, but the network will not always
  // be one zone.
  const first = new Date(asIfUtc - offsetMinutes(tz, new Date(asIfUtc)) * 60_000)
  return new Date(asIfUtc - offsetMinutes(tz, first) * 60_000)
}

/** "just now", "3 min ago", "2 h 15 min ago", then hands over to relativeDays.
 *
 *  relativeDays is calendar-day granular, which is right for a station card
 *  and useless for a live panel: a reading taken four minutes ago and one
 *  taken nineteen hours ago both render as "today". Minutes are what tell a
 *  reader whether they are looking at live data or at a node that stopped
 *  reporting over lunch.
 */
export function relativeAge(value: string | null, now: Date = new Date(),
                            tz = STATION_TZ): string {
  if (!value) return 'never'
  const at = new Date(value).getTime()
  if (Number.isNaN(at)) return 'never'

  const ms = now.getTime() - at
  // The gateway's clock and this browser's are not synchronised, so a reading
  // stamped slightly ahead of us is a clock difference, not data from the
  // future. Announcing it would read as a fault in the archive.
  if (ms < 60_000) return 'just now'

  const minutes = Math.floor(ms / 60_000)
  if (minutes < 60) return `${minutes} min ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    const rest = minutes % 60
    return rest ? `${hours} h ${rest} min ago` : `${hours} h ago`
  }
  // Handed the same `now`, not the wall clock: a caller that fixed the
  // instant for this panel must get the same answer from the day-granular
  // half, or the two disagree across the 24-hour boundary.
  return relativeDays(value, tz, now)
}

/** The city in a zone name, for the header clock: "Asia/Tbilisi" is
 *  "Tbilisi", "America/New_York" is "New York", a zone with no slash is
 *  itself. */
export function zoneCity(tz = STATION_TZ): string {
  const city = tz.split('/').pop() ?? tz
  return city.replace(/_/g, ' ')
}

/** "14:32 Tbilisi": the header clock, in station time and to the minute,
 *  so a reader in another zone sees the wall clock every timestamp on the
 *  page is written in. */
export function stationClock(now: Date, tz = STATION_TZ): string {
  return `${formatTime(now, tz)} ${zoneCity(tz)}`
}
